// src/components/GuideCard/GuideDrawer.js
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ReactGA from 'react-ga4';
import { endpoints, CHROME_STORE_URL } from '../../services/api';
import { detectExtension, playWithExtension } from '../../services/extension';

// ── Small reusable action button ─────────────────────────────────────────────
const ActionBtn = ({ onClick, icon, label, variant = 'default', disabled = false, loading = false }) => (
  <button
    className={`gdrawer__action-btn gdrawer__action-btn--${variant}`}
    onClick={onClick}
    disabled={disabled || loading}
    aria-label={label}
  >
    {loading ? (
      <span className="gdrawer__spinner" />
    ) : icon}
    <span>{label}</span>
  </button>
);

// ── Main drawer ───────────────────────────────────────────────────────────────
const GuideDrawer = ({
  guide,
  isOwner          = false,
  showDelete       = false,
  showDownload     = false,
  onDelete,
  onDownload,
  onDownloadPrivacy,
  userPlan,
  onShare,
  onEdit,
  blurPreview      = false,
  onClose,
}) => {
  const { token } = useAuth();
  const navigate  = useNavigate();

  const [imgError,      setImgError]      = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pdfLoading,          setPdfLoading]          = useState(false);
  const [pdfPrivacyLoading,   setPdfPrivacyLoading]   = useState(false);
  const [videoLoading,        setVideoLoading]        = useState(false);
  const [videoError,          setVideoError]          = useState('');
  const [videoPrivacyLoading, setVideoPrivacyLoading] = useState(false);
  const [copied,        setCopied]        = useState(false);
  const [showMore,      setShowMore]      = useState(false);
  const [playCount,     setPlayCount]     = useState(null);
  const [visible,       setVisible]       = useState(false); // for CSS enter animation
  const [playState,     setPlayState]     = useState('idle');
  const [playError,     setPlayError]     = useState('');
  const playData = useRef(null);

  const drawerRef = useRef(null);

  // ── Animate in ──────────────────────────────────────────────────────────────
  useEffect(() => {
    // tick needed so the CSS transition fires
    const t = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(t);
  }, []);

  // ── Close on Escape ─────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handler);
    // Lock body scroll
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, []); // eslint-disable-line

  // ── Fetch play count (owner only) ───────────────────────────────────────────
  useEffect(() => {
    if (!token || !isOwner) return;
    fetch(endpoints.analytics.guide(guide.id), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setPlayCount(data.total_plays ?? 0); })
      .catch(() => {});
  }, [guide.id, token, isOwner]);

  // ── Playback status from the extension ──────────────────────────────────────
  useEffect(() => {
    const onStatus = (e) => {
      const { status, error: err } = e.detail || {};
      if (status === 'navigating')    setPlayState('launching');
      if (status === 'waiting_login') setPlayState('waiting_login');
      if (status === 'started')       setPlayState('playing');
      if (status === 'error') {
        setPlayState('error');
        setPlayError(err || 'Something went wrong. Please try again.');
      }
    };
    window.addEventListener('michii:play_status', onStatus);
    return () => window.removeEventListener('michii:play_status', onStatus);
  }, []);

  // ── Animate out then call onClose ───────────────────────────────────────────
  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 280); // match CSS transition duration
  };

  // ── Actions ─────────────────────────────────────────────────────────────────
  // Plays right here, same as the guide page: no /play hop. Always re-detects —
  // the answer changes the moment the user installs the extension.
  const handlePlay = async () => {
    const installed = await detectExtension();

    if (!installed) {
      ReactGA.event('chrome_store_click', { click_source: 'guide_drawer_play' });
      window.open(CHROME_STORE_URL, '_blank', 'noopener');
      setPlayState('needs_extension');
      return;
    }

    setPlayError('');
    setPlayState('launching');

    // The card's guide carries no steps — the extension replays the play payload.
    if (!playData.current) {
      try {
        const res = await fetch(endpoints.guides.play(guide.id), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) playData.current = await res.json();
      } catch (_) {}
    }
    playWithExtension(playData.current || guide);
  };

  const handleOpenPage = () => {
    navigate(`/guides/${guide.id}`, { state: { guide } });
    handleClose();
  };

  const handleCopyShortcut = async () => {
    if (!guide.shortcut) return;
    try {
      await navigator.clipboard.writeText(guide.shortcut);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_) {}
  };

  const handlePdfDownload = async () => {
    setPdfLoading(true);
    try {
      const res = await fetch(endpoints.guides.exportPdf(guide.id), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('PDF export failed.');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      const safe = (guide.name || 'guide').replace(/\s+/g, '_').replace(/[^A-Za-z0-9._-]/g, '') || 'guide';
      a.download = `${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setVideoError(err.message);
      setTimeout(() => setVideoError(''), 4000);
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePdfPrivacyDownload = async () => {
    setPdfPrivacyLoading(true);
    try {
      const res = await fetch(`${endpoints.guides.exportPdf(guide.id)}?privacy=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('PDF export failed.');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      const safe = (guide.name || 'guide').replace(/\s+/g, '_').replace(/[^A-Za-z0-9._-]/g, '') || 'guide';
      a.download = `${safe}_private.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setVideoError(err.message);
      setTimeout(() => setVideoError(''), 4000);
    } finally {
      setPdfPrivacyLoading(false);
    }
  };

  const handleVideoDownload = async () => {
    setVideoLoading(true);
    setVideoError('');
    try {
      const res = await fetch(endpoints.guides.exportVideo(guide.id), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Video export failed.');
      }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      const safe = (guide.name || 'guide').replace(/\s+/g, '_').replace(/[^A-Za-z0-9._-]/g, '') || 'guide';
      a.download = `${safe}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setVideoError(err.message);
      setTimeout(() => setVideoError(''), 4000);
    } finally {
      setVideoLoading(false);
    }
  };

  const handleVideoPrivacyDownload = async () => {
    setVideoPrivacyLoading(true);
    setVideoError('');
    try {
      const res = await fetch(`${endpoints.guides.exportVideo(guide.id)}?privacy=true`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Video export failed.');
      }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      const safe = (guide.name || 'guide').replace(/\s+/g, '_').replace(/[^A-Za-z0-9._-]/g, '') || 'guide';
      a.download = `${safe}_private.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setVideoError(err.message);
      setTimeout(() => setVideoError(''), 4000);
    } finally {
      setVideoPrivacyLoading(false);
    }
  };

  const handleDelete = () => {
    setConfirmDelete(false);
    onDelete && onDelete(guide.id);
    handleClose();
  };

  const hasThumbnail = guide.thumbnail_path && !imgError;
  const stepCount    = guide.steps?.length ?? 0;

  // ── Portal render ────────────────────────────────────────────────────────────
  return ReactDOM.createPortal(
    <div
      className={`gdrawer__backdrop ${visible ? 'gdrawer__backdrop--visible' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
      aria-modal="true"
      role="dialog"
      aria-label={`${guide.name} details`}
    >
      <aside
        ref={drawerRef}
        className={`gdrawer ${visible ? 'gdrawer--visible' : ''}`}
      >
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="gdrawer__header">
          <button className="gdrawer__close" onClick={handleClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <button className="gdrawer__open-page" onClick={handleOpenPage} aria-label="Open full page">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
            Open full page
          </button>
        </div>

        {/* ── Thumbnail ────────────────────────────────────────────────── */}
        <div className="gdrawer__thumb">
          {hasThumbnail ? (
            <img
              src={guide.thumbnail_path}
              alt={`${guide.name} preview`}
              className={`gdrawer__thumb-img${blurPreview ? ' privacy-blur' : ''}`}
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="gdrawer__thumb-placeholder">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
            </div>
          )}
        </div>

        {/* ── Guide meta ───────────────────────────────────────────────── */}
        <div className="gdrawer__body">
          {/* title + badge row */}
          <div className="gdrawer__title-row">
            <h2 className="gdrawer__title">{guide.name}</h2>
            {guide.is_public
              ? <span className="m-badge m-badge--public">Public</span>
              : guide.shared_emails?.length > 0
                ? <span className="m-badge m-badge--shared">Shared</span>
                : <span className="m-badge m-badge--private">Private</span>
            }
          </div>

          {/* description */}
          {guide.description && (
            <p className="gdrawer__desc">{guide.description}</p>
          )}

          {/* step count + play count */}
          <div className="gdrawer__stats">
            {/* shortcut sits in the meta row, not alone on its own line */}
            <button
              className={`gdrawer__shortcut ${copied ? 'gdrawer__shortcut--copied' : ''}`}
              onClick={handleCopyShortcut}
              title="Click to copy"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
              {copied ? 'Copied!' : guide.shortcut}
            </button>
            <span className="gdrawer__stat">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/>
                <line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/>
                <line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
              </svg>
              {stepCount} {stepCount === 1 ? 'step' : 'steps'}
            </span>
            {isOwner && playCount !== null && (
              <span className="gdrawer__stat">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <polygon points="5 3 19 12 5 21 5 3"/>
                </svg>
                {playCount === 0 ? 'No plays' : `${playCount} ${playCount === 1 ? 'play' : 'plays'}`}
              </span>
            )}
          </div>

          {/* ── Primary action: Play ──────────────────────────────────── */}
          <button className="gdrawer__play-btn" onClick={handlePlay} disabled={playState === 'launching'}>
            {playState === 'launching'
              ? <span className="gdrawer__spinner" />
              : <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                  <polygon points="5 3 19 12 5 21 5 3"/>
                </svg>
            }
            {playState === 'launching' ? 'Opening…' : 'Play Guide'}
          </button>

          {playState === 'waiting_login' && (
            <p className="gdrawer__play-status">Sign in on the site, then playback continues automatically.</p>
          )}
          {playState === 'playing' && (
            <p className="gdrawer__play-status">Follow the highlighted steps in the new browser tab.</p>
          )}
          {playState === 'needs_extension' && (
            <p className="gdrawer__play-status gdrawer__play-status--warn">
              Playing needs the Michii extension. We opened the{' '}
              <a href={CHROME_STORE_URL} target="_blank" rel="noreferrer">Chrome Web Store</a> —
              install it, reload this page, then hit Play.
            </p>
          )}
          {playState === 'error' && (
            <p className="gdrawer__play-status gdrawer__play-status--warn">
              {playError} <button className="gdrawer__link-btn" onClick={handlePlay}>Try again</button>
            </p>
          )}

          {/* ── Secondary actions: two up front, the rest under More ── */}
          {(() => {
            const primary = [
              showDownload && (
                <ActionBtn
                  onClick={handlePdfDownload}
                  label={pdfLoading ? 'Exporting…' : 'Download PDF'}
                  variant="default"
                  loading={pdfLoading}
                  icon={
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                  }
                />
              ),
              isOwner && onShare && (
                <ActionBtn
                  onClick={() => { onShare(guide); handleClose(); }}
                  label="Share Guide"
                  variant="default"
                  icon={
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                    </svg>
                  }
                />
              ),
            ].filter(Boolean);
            const extra = [
              showDownload && (
                <ActionBtn
                  onClick={handleVideoDownload}
                  label={videoLoading ? 'Exporting…' : 'Download Video'}
                  variant="default"
                  loading={videoLoading}
                  icon={
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                    </svg>
                  }
                />
              ),
              showDownload && userPlan && userPlan !== 'free' && (
                <ActionBtn
                  onClick={handlePdfPrivacyDownload}
                  label={pdfPrivacyLoading ? 'Exporting…' : 'PDF (Privacy)'}
                  variant="default"
                  loading={pdfPrivacyLoading}
                  icon={
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="9" y1="14" x2="15" y2="14"/><line x1="9" y1="18" x2="11" y2="18"/>
                    </svg>
                  }
                />
              ),
              showDownload && userPlan && userPlan !== 'free' && (
                <ActionBtn
                  onClick={handleVideoPrivacyDownload}
                  label={videoPrivacyLoading ? 'Exporting…' : 'Video (Privacy)'}
                  variant="default"
                  loading={videoPrivacyLoading}
                  icon={
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                      <line x1="5" y1="9" x2="11" y2="9"/>
                    </svg>
                  }
                />
              ),
              isOwner && onEdit && (
                <ActionBtn
                  onClick={() => { onEdit(guide); handleClose(); }}
                  label="Edit Guide"
                  variant="default"
                  icon={
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                  }
                />
              ),
              isOwner && (
                <ActionBtn
                  onClick={() => { navigate(`/analytics/${guide.id}`); handleClose(); }}
                  label="View Analytics"
                  variant="default"
                  icon={
                    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                      <rect x="1" y="9" width="3" height="6" rx="1" fill="currentColor"/>
                      <rect x="6" y="5" width="3" height="10" rx="1" fill="currentColor"/>
                      <rect x="11" y="1" width="3" height="14" rx="1" fill="currentColor"/>
                    </svg>
                  }
                />
              ),
              showDelete && isOwner && (
                <ActionBtn
                  onClick={() => setConfirmDelete(true)}
                  label="Delete Guide"
                  variant="danger"
                  icon={
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                  }
                />
              ),
            ].filter(Boolean);
            if (!primary.length && !extra.length) return null;
            return (
              <div className="gdrawer__actions-wrap">
                <div className="gdrawer__actions">
                  {primary.map((btn, k) => <React.Fragment key={`p${k}`}>{btn}</React.Fragment>)}
                  {extra.length > 0 && (
                    <button
                      type="button"
                      className="gdrawer__more"
                      onClick={() => setShowMore(v => !v)}
                      aria-expanded={showMore}
                      aria-label={showMore ? 'Fewer actions' : 'More actions'}
                      title={showMore ? 'Fewer actions' : 'More actions'}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.9" /><circle cx="12" cy="12" r="1.9" /><circle cx="19" cy="12" r="1.9" /></svg>
                    </button>
                  )}
                </div>
                {showMore && extra.length > 0 && (
                  <div className="gdrawer__menu" role="group" aria-label="More actions">
                    {extra.map((btn, k) => <React.Fragment key={`e${k}`}>{btn}</React.Fragment>)}
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* ── Confirm delete ───────────────────────────────────────────── */}
        {confirmDelete && (
          <div className="gdrawer__confirm">
            <p>Delete <strong>{guide.name}</strong>? This cannot be undone.</p>
            <div className="gdrawer__confirm-actions">
              <button className="gdrawer__confirm-cancel" onClick={() => setConfirmDelete(false)}>Keep it</button>
              <button className="gdrawer__confirm-delete" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        )}

        {/* ── Video error toast ────────────────────────────────────────── */}
        {videoError && (
          <div className="gdrawer__toast gdrawer__toast--error">{videoError}</div>
        )}
      </aside>
    </div>,
    document.body
  );
};

export default GuideDrawer;